import React, { useState, useCallback, useMemo } from "react";
import { emdash, formatMeasure } from "../../utils/Constants/systemSetting";
import {
  toDollar,
  customParseFloat,
} from "../../utils/Constants/systemSetting";
import { exportToCSV } from "../../utils/ExecutiveData/csvExport";
import { Button } from "antd";

export function ExecutivePerksSummaryStatTable({
  companyInfo,
  executivePerksData,
  executivePerksColumns,
  PDFprocessing,
}) {
  const [customPercentile, setCustomPercentile] = useState(0);

  const execPerksSummaryStatColumns = [
    {
      title: "Compensation Type",
      dataIndex: "Compensation Type",
      key: "Compensation Type",
    },
    {
      title: "Min",
      dataIndex: "Min",
      key: "Min",
    },
    {
      title: "25th",
      dataIndex: "25th",
      key: "25th",
    },
    {
      title: "50th",
      dataIndex: "50th",
      key: "50th",
    },
    {
      title: "75th",
      dataIndex: "75th",
      key: "75th",
    },
    {
      title: "Max",
      dataIndex: "Max",
      key: "Max",
    },
    {
      title: "Average",
      dataIndex: "Average",
      key: "Average",
    },
    {
      title: "n",
      dataIndex: "n",
      key: "n",
    },
    {
      title: "Perk Prevalence",
      dataIndex: "Perk Prevalence",
      key: "Perk Prevalence",
    },
    {
      title: "Custom Percentile",
      dataIndex: "Custom Percentile",
      key: "Custom Percentile",
    },
  ];

  const [execPerksSummaryStatData, setExecPerkSummaryStatData] = useState([]);

  const formatDataForCSVexport = useCallback((awardsColumns, awardsData) => {
    const compensationDecisionOutputData = [];
    if (awardsData?.length) {
      compensationDecisionOutputData.push(
        ...awardsData?.map((item) => {
          return { ...item };
        }),
      );
    }
    const compensationDecisionHeaders = [];
    awardsColumns.forEach((item) => {
      if (!compensationDecisionHeaders.includes(item.dataIndex)) {
        compensationDecisionHeaders.push(item.dataIndex);
      }
    });
    const formattedBoardPayData = compensationDecisionOutputData.map((item) => {
      const formattedItem = {};
      awardsColumns.forEach((keyDetails, index) => {
        if (keyDetails?.dataIndex) {
          const itemData =
            item?.[keyDetails.dataIndex]?.props?.children?.props?.children
              ?.props?.children || item?.[keyDetails.dataIndex];

          formattedItem[`"${keyDetails.title}"`] =
            itemData && (itemData.length > 2 || itemData >= 0)
              ? itemData.toString()?.replaceAll(",", "")
              : "";
        }
      });
      return formattedItem;
    });
    return formattedBoardPayData;
  }, []);

  const renderSummaryStatsTable = useCallback(
    (data) => {
      const table = {};
      executivePerksColumns.forEach((item) => {
        if (!["name", "position", "Company"].includes(item.dataIndex)) {
          table[item.dataIndex] = [];
        }
      });

      for (let i = 0; i < data.length; i++) {
        for (let key in table) {
          let item = data[i]?.[key];
          if (item) {
            const actualValue =
              item?.props?.children?.props?.children?.props?.children;
            if (actualValue) {
              const intValue = parseInt(
                actualValue.replace("$", "").replace(",", ""),
              );
              table[key].push(intValue);
            }
          }
        }
      }

      const quantile = (sorted, q) => {
        const pos = (sorted.length - 1) * q;
        const base = Math.floor(pos);
        const rest = pos - base;

        if (sorted[base + 1] !== undefined) {
          return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
        } else {
          return sorted[base];
        }
      };

      const newExecPerkSummaryStatData = [];

      const formattedData = Object.entries(table)
        .filter(([key, value]) => value.length)
        .map(([key, value], index) => {
          const sorted = value.sort((a, b) => a - b);
          const len = sorted.length;
          const total = value.reduce((total, arg) => total + parseInt(arg), 0);

          const min = toDollar(sorted[0]);
          const max = toDollar(sorted[len - 1]);
          const oneQuarter = toDollar(quantile(sorted, 0.25));
          const half = toDollar(quantile(sorted, 0.5));
          const threeQuarter = toDollar(quantile(sorted, 0.75));
          const customPercentileValue = toDollar(
            quantile(sorted, customPercentile / 100),
          );

          const totalLen = Array.isArray(data) ? data.length : 0;
          const rawPrevalence = len > totalLen ? 100 : (100 * len) / totalLen;
          const prevalence = totalLen
            ? formatMeasure(rawPrevalence, "percent", true, 0)
            : emdash;

          const newData = {
            "Compensation Type": key,
            Min: min,
            "25th": oneQuarter,
            "50th": half,
            "75th": threeQuarter,
            Max: max,
            Average: Math.round(total / len),
            n: len,
            "Perk Prevalence": prevalence,
            "Custom Percentile": customPercentileValue,
          };
          newExecPerkSummaryStatData.push(newData);

          return (
            <tr key={index}>
              <th style={{ textAlign: "center" }}>{key}</th>
              <td>{min || emdash}</td>
              <td className="d-none d-lg-table-cell">{oneQuarter || emdash}</td>
              <td className="d-none d-lg-table-cell">{half || emdash}</td>
              <td className="d-none d-lg-table-cell">
                {threeQuarter || emdash}
              </td>
              <td>{max || emdash}</td>
              <td>{toDollar(Math.round(total / len)) || emdash}</td>
              <td>{len || 0}</td>
              <td>{prevalence || emdash}</td>
              <td>{customPercentileValue || emdash}</td>
            </tr>
          );
        });

      setExecPerkSummaryStatData(newExecPerkSummaryStatData);
      return formattedData;
    },
    [customPercentile, executivePerksColumns],
  );

  const renderedSummaryStatData = useMemo(() => {
    return renderSummaryStatsTable(executivePerksData);
  }, [executivePerksData, renderSummaryStatsTable]);

  return (
    <div className="c-table">
      <div className="summary-table">
        {!PDFprocessing && execPerksSummaryStatData.length > 0 && (
          <div style={{ width: "100%" }}>
            <Button
              style={{ marginBottom: 10 }}
              onClick={() =>
                exportToCSV(
                  formatDataForCSVexport(
                    execPerksSummaryStatColumns,
                    execPerksSummaryStatData,
                  ),
                  `${companyInfo?.Ticker} Company-Wide Perks ${
                    new Date().toString().split("GMT-")[0]
                  }.csv`,
                )
              }
            >
              Export Exec Perks Summary Stats as CSV
            </Button>
          </div>
        )}
        <div className="summary-stat-table">
          <table>
            <thead>
              <tr className="th-row">
                <th>Compensation Type</th>
                <th>Min</th>
                <th className="d-none d-lg-table-cell">25th</th>
                <th className="d-none d-lg-table-cell">50th</th>
                <th className="d-none d-lg-table-cell">75th</th>
                <th>Max</th>
                <th>Average</th>
                <th>n</th>
                <th style={{ width: "50px" }}>Perk Prevalence</th>
                <th style={{ width: 100 }}>
                  Custom Percentile
                  <input
                    className="custom"
                    type="number"
                    name="custom"
                    value={customPercentile}
                    onChange={(e) => {
                      let value = customParseFloat(e.target.value);
                      if (value < 0) {
                        value = 0;
                      } else if (value > 100) {
                        value = 100;
                      }
                      setCustomPercentile(value.toLocaleString());
                    }}
                  />
                </th>
              </tr>
            </thead>
            <tbody>{renderedSummaryStatData}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
